import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { canonical, webPageSchema } from "@/utils/seo";

const partnerBenefits = [
  {
    icon: "ri-eye-line",
    title: "精準曝光",
    description:
      "透過神秘客實測文章，讓目標客群了解你的店家特色，比傳統廣告更有說服力。",
  },
  {
    icon: "ri-star-smile-line",
    title: "真實口碑",
    description:
      "我們的評比建立在真實體驗之上，優質店家自然獲得正面推薦，建立長期品牌信任。",
  },
  {
    icon: "ri-article-line",
    title: "內容行銷",
    description:
      "專業編輯團隊將你的店家故事轉化為高品質內容，持續在搜尋引擎與社群中曝光。",
  },
  {
    icon: "ri-user-add-line",
    title: "讀者導流",
    description:
      "文章內提供店家資訊與官方連結，讓感興趣的讀者直接前往你的官方頁面。",
  },
];

const coopTypes = [
  {
    title: "神秘客評比",
    desc: "由我們派出神秘客實際體驗，撰寫專屬評比文章，深度介紹你的店家特色。",
    price: "依專案規模報價",
  },
  {
    title: "品牌專欄",
    desc: "在你的主題分類下建立店家專屬頁面，展示店家故事、特色服務與讀者評價。",
    price: "月費制",
  },
  {
    title: "活動合作",
    desc: "聯合舉辦體驗活動、快閃優惠或限時折扣，透過社群與 EDM 大量曝光。",
    price: "依活動規模報價",
  },
];

export default function PartnersPage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="w-full">
      <SEO
        meta={{
          title: "商家合作提案｜美好時光指南",
          description: "與美好時光指南合作，透過神秘客實測評比與內容曝光，讓更多讀者認識你的優質店家。",
          canonical: canonical("/partners"),
          keywords: ["商家合作", "品牌曝光", "內容合作", "美好時光指南"],
          structuredData: webPageSchema("商家合作提案｜美好時光指南", "與美好時光指南合作，透過神秘客實測評比與內容曝光。", "/partners"),
        }}
      />

      {/* Hero */}
      <div className="w-full bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-20 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            與我們一起，讓更多人發現好店家
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto mb-8">
            美好時光指南每月有超過 50 萬名活躍讀者，他們正在尋找值得體驗的好活動、好店家。成為我們的內容合作夥伴，讓你的優質店家被看見。
          </p>
          <a
            href="mailto:partners@goldentimeguide.com"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            <i className="ri-mail-send-line" />
            聯繫合作部門
          </a>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">
            為什麼與我們合作？
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerBenefits.map((b) => (
              <div
                key={b.title}
                className="bg-white rounded-xl p-6 border border-slate-100 hover:border-primary/20 transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                  <i className={`${b.icon} text-xl`} />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {b.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cooperation Types */}
      <div className="w-full bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">
            合作方式
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coopTypes.map((c, idx) => (
              <div
                key={c.title}
                className={`bg-white rounded-xl p-6 border transition-all ${
                  idx === 0
                    ? "border-primary/30 ring-1 ring-primary/10"
                    : "border-slate-100"
                }`}
              >
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  {c.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">
                  {c.desc}
                </p>
                <span className="inline-block px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600">
                  {c.price}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16 text-center">
        <h2 className="text-2xl font-bold text-slate-800 mb-3">
          準備好讓更多顧客認識你了嗎？
        </h2>
        <p className="text-slate-500 mb-6">
          填寫簡單的合作意向表單，我們的商務團隊會在 2 個工作日內與你聯繫。
        </p>
        <a
          href="mailto:partners@goldentimeguide.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          <i className="ri-mail-send-line" />
          立即聯繫
        </a>
      </div>
    </div>
  );
}